import React, { useState, useEffect, useRef } from 'react';
import './CTA.css';
import NewProjectModalV2 from '../Dashboard/NewProjectModalV2.jsx';
import { projectService } from '../../services/project.service.js';
import SubscriptionService from '../../services/subscription.service';

const CTA = ({ userCoins, onCoinsUpdate }) => {
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userPlan, setUserPlan] = useState('free');
    const [activeFeature, setActiveFeature] = useState(0);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const editorRef = useRef(null);

    // Récupérer les langues disponibles
    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                setIsLoading(true);
                const languagesData = await projectService.getLanguages();
                setLanguages(languagesData);
            } catch (error) {
                console.error('Erreur lors du chargement des langages:', error);
                setError('Une erreur est survenue lors du chargement des langues disponibles');
            } finally {
                setIsLoading(false);
            }
        };

        fetchLanguages();
    }, []);

    // Récupérer le plan d'abonnement de l'utilisateur
    useEffect(() => {
        const fetchUserSubscription = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.log('Utilisateur non connecté, utilisation du plan gratuit par défaut');
                    return;
                }

                const subscription = await SubscriptionService.getCurrentSubscription();
                if (subscription && subscription.planId) {
                    setUserPlan(subscription.planId);
                    console.log('Plan d\'abonnement récupéré:', subscription.planId);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du plan d\'abonnement:', error);
            }
        };

        fetchUserSubscription();
    }, []);

    // Rotation automatique des fonctionnalités avec une transition plus fluide
    useEffect(() => {
        let interval;

        // Ne pas tourner automatiquement pendant le survol
        if (!isHovering) {
            interval = setInterval(() => {
                setActiveFeature((prev) => (prev + 1) % editorFeatures.length);
            }, 6000);
        }

        return () => clearInterval(interval);
    }, [isHovering]);

    // Gestion de la position de la souris sur l'éditeur avec une réactivité améliorée
    const handleMouseMove = (e) => {
        if (!editorRef.current) return;

        const rect = editorRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) - 0.5;
        const y = ((e.clientY - rect.top) / rect.height) - 0.5;

        // Ajout d'un facteur de lissage pour un mouvement plus naturel
        setMousePosition({
            x: x * 0.7, // Réduit l'amplitude du mouvement pour plus de subtilité
            y: y * 0.7
        });

        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setMousePosition({ x: 0, y: 0 });
        setIsHovering(false);
    };

    // Gestionnaire pour la création de projet
    const handleProjectCreated = () => {
        console.log("CTA: Projet créé avec succès");
        setIsProjectModalOpen(false);
    };

    // Gestionnaire pour la mise à jour des tokens
    const handleCoinsUpdated = (newCoins) => {
        console.log("CTA: Mise à jour des tokens:", newCoins);
        if (typeof onCoinsUpdate === 'function') {
            onCoinsUpdate(newCoins);
        }
    };

    const handleClick = () => {
        setIsProjectModalOpen(true);
    };

    const editorFeatures = [
        {
            id: 0,
            title: "Doublage automatique IA",
            description: "Transformez vos contenus avec un doublage de qualité professionnelle dans plus de 30 langues, généré par notre IA avancée.",
            icon: "microphone-alt"
        },
        {
            id: 1,
            title: "Éditeur professionnel",
            description: "Interface intuitive permettant de découper, fusionner et réorganiser des segments audio avec une précision au millième de seconde.",
            icon: "cut"
        },
        {
            id: 2,
            title: "Imports audio personnalisés",
            description: "Importez vos propres enregistrements de référence pour créer des doublages personnalisés ou remplacer certains segments.",
            icon: "file-audio"
        },
        {
            id: 3,
            title: "Sous-titres synchronisés",
            description: "Génération automatique de sous-titres parfaitement synchronisés avec l'audio dans toutes les langues supportées.",
            icon: "closed-captioning"
        },

    ];

    // Calcul des styles de transformation 3D basés sur la position de la souris
    const editorStyle = {
        transform: `perspective(1500px) rotateY(${mousePosition.x * 5}deg) rotateX(${-mousePosition.y * 5}deg) scale(${isHovering ? 1.01 : 1})`,
        transition: mousePosition.x === 0 && mousePosition.y === 0 ? 'transform 0.8s ease-out' : 'transform 0.1s ease-out'
    };

    return (
        <section className="studio-cta-section">
            <div className="studio-cta-background">
                <div className="studio-cta-gradient"></div>
                <div className="studio-cta-mesh"></div>
                <div className="studio-cta-particles"></div>
            </div>

            <div className="studio-cta-container">
                <div className="studio-cta-header">
                    <h2 className="studio-cta-title">Studio d'édition <span className="studio-text-gradient">tout-en-un</span></h2>
                    <p className="studio-cta-description">
                        La solution complète pour transformer vos vidéos et atteindre une audience internationale
                        avec notre technologie de doublage IA et notre puissant éditeur intégré.
                    </p>
                </div>

                <div className="studio-cta-content-wrapper">
                    <div className="studio-cta-preview-container studio-floating-animation">
                        <div
                            className="studio-editor-preview"
                            style={editorStyle}
                            ref={editorRef}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className="studio-editor-header">
                                <div className="studio-editor-controls">
                                    <span className="studio-editor-control studio-red"></span>
                                    <span className="studio-editor-control studio-yellow"></span>
                                    <span className="studio-editor-control studio-green"></span>
                                </div>
                                <div className="studio-editor-title">Studio de montage</div>
                            </div>
                            <div className="studio-editor-body">
                                <div className="studio-video-preview">
                                    <div className="studio-video-placeholder">
                                        <div className="studio-play-button"></div>
                                    </div>
                                </div>
                                <div className="studio-timeline">
                                    <div className="studio-timeline-ruler"></div>
                                    <div className="studio-timeline-tracks">
                                        <div className="studio-timeline-track studio-video-track">
                                            <div className="studio-track-label">Vidéo</div>
                                            <div className="studio-track-content">
                                                <div className="studio-video-segment"></div>
                                            </div>
                                        </div>
                                        <div className="studio-timeline-track studio-audio-track">
                                            <div className="studio-track-label">Audio</div>
                                            <div className="studio-track-content">
                                                <div className="studio-audio-segment studio-segment-1"></div>
                                                <div className="studio-audio-segment studio-segment-2"></div>
                                                <div className="studio-audio-segment studio-segment-3"></div>
                                            </div>
                                        </div>
                                        <div className="studio-timeline-track studio-subtitle-track">
                                            <div className="studio-track-label">Sous-titres</div>
                                            <div className="studio-track-content">
                                                <div className="studio-subtitle-segment"></div>
                                                <div className="studio-subtitle-segment"></div>
                                                <div className="studio-subtitle-segment"></div>
                                                <div className="studio-subtitle-segment"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="studio-features-container">

                        <ul className="studio-features-list">
                            {editorFeatures.map((feature) => (
                                <li
                                    key={feature.id}
                                    className={`studio-feature-item ${activeFeature === feature.id ? 'studio-active' : ''}`}
                                    onClick={() => setActiveFeature(feature.id)}
                                    onMouseEnter={() => setActiveFeature(feature.id)}
                                    data-feature={feature.icon}
                                >
                                    <div className="studio-feature-icon">
                                        <i className={`fas fa-${feature.icon}`}></i>
                                    </div>
                                    <div className="studio-feature-content">
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="studio-cta-actions">
                            <button
                                className="studio-cta-button"
                                onClick={handleClick}
                            >
                                <i className="fas fa-rocket studio-button-icon"></i>
                                Commencer un projet
                            </button>
                            {error && <div className="studio-error-message">{error}</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ajout d'un indicateur de chargement */}
            {isLoading && (
                <div className="studio-loading-overlay">
                    <div className="studio-loading-spinner"></div>
                </div>
            )}

            <NewProjectModalV2
                isOpen={isProjectModalOpen}
                onClose={() => setIsProjectModalOpen(false)}
                onProjectCreated={handleProjectCreated}
                onCoinsUpdated={handleCoinsUpdated}
                allLanguages={languages}
                userCoins={userCoins}
                userPlan={userPlan}
            />
        </section>
    );
};

export default CTA;